import React, { Component, ErrorInfo, ReactNode } from 'react';
import ErrorLogger from '../services/ErrorLogger';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  errorId?: string;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(_: Error): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const errorIdPromise = this.logError(error, errorInfo);
    this.setState({ errorId: 'reporting...' });
    errorIdPromise.then((id) => {
      this.setState({ errorId: id });
    }).catch(() => {
      this.setState({ errorId: 'failed' });
    });
  }

  private async logError(error: Error, errorInfo: ErrorInfo): Promise<string> {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      
      await ErrorLogger.logError(error, {
        user: user.id ? { id: user.id, email: user.email } : undefined,
        request: {
          url: window.location.href,
          method: 'GET',
          headers: {},
          body: { componentStack: errorInfo.componentStack }
        }
      });
      
      return 'logged';
    } catch (logError) {
      console.error('Failed to log error:', logError);
      return 'failed';
    }
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
          <div className="max-w-md w-full bg-white dark:bg-gray-800 shadow-lg rounded-lg p-6">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0">
                <svg className="h-8 w-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Something went wrong
                </h3>
              </div>
            </div>
            
            <div className="text-sm text-gray-600 dark:text-gray-300 mb-4">
              An unexpected error occurred. The development team has been notified and will investigate this issue.
            </div>
            
            {this.state.errorId && (
              <div className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                Error ID: {this.state.errorId}
              </div>
            )}
            
            <div className="flex space-x-3">
              <button
                onClick={() => window.location.reload()}
                className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Reload Page
              </button>
              <button
                onClick={() => window.history.back()}
                className="flex-1 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-200 px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-400 dark:hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                Go Back
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;